
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { applicationStoreGetter } from '@/store/application.store';
import Company from '@/models/Company';
import { driverStoreActions, driverStoreGetter } from '@/store/driver.store';
import Driver from '@/models/Driver';
import moment from 'moment/moment';
import DriverRepository from '@/api/repositories/DriverRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import { UserRoles } from '@/enum/UserRoles';
import Promoter from '@/models/Promoter';
import { csvFileName, downloadCsv, toCsv } from '@/misc/csv';
import { downloadTablePdf, reportSubtitle } from '@/misc/pdf';
import { ReportColumn, reportFileName } from '@/misc/report';

const ApplicationStore = namespace('application');
const DriverStore = namespace('driver');

const driverRepository: DriverRepository = RepositoryFactory.get('driver');

@Component({
    components: {
        DriverTableComponent: () => import(
            /* webpackChunkName: "DriverTableComponent" */
            '@/components/DriverTable.component.vue'),
        EditDriverComponent: () => import(
            /* webpackChunkName: "EditDriverComponent" */
            '@/components/EditDriver.component.vue')
    }
})
export default class DriversView extends Vue {
    @ApplicationStore.Getter(applicationStoreGetter.SELECTED_COMPANY)
    public selectedCompany!: Company;

    @DriverStore.Action(driverStoreActions.LIST)
    public loadAllDrivers!: (payload: { company: Company, from: string, to: string, withDeleted: boolean }) => Promise<Driver[]>;

    @DriverStore.Getter(driverStoreGetter.DRIVERS)
    public allDrivers!: Driver[];

    public startMenu: boolean = false;
    public endMenu: boolean = false;
    public startDate: string | null = null;
    public endDate: string | null = null;

    public isLoading: boolean = false;

    public searchValue: string = '';
    public showCreationDialog: boolean = false;

    public showDeletedDrivers: boolean = false;

    public async created() {
        const today = moment(new Date());
        this.endDate = today.format('yyyy-MM-DD');
        today.set({ date: 1 });
        this.startDate = today.format('yyyy-MM-DD');
        await this.loadDrivers();
    }

    public async closeCreationDialog(reload: boolean) {
        this.showCreationDialog = false;
        if (reload) {
            await this.loadDrivers();
        }
    }

    public async recycleDriver(driver: Driver) {
        await driverRepository.recycle({ id: driver.id });
        await this.loadDrivers();
    }

    private async loadDrivers(): Promise<void> {
        try {
            this.isLoading = true;
            await this.loadAllDrivers({
                company: this.selectedCompany,
                from: this.startDate!,
                to: this.endDate!,
                withDeleted: this.showDeletedDrivers
            });
        } catch (e) {
            console.log(e);
        } finally {
            this.isLoading = false;
        }
    }

    private get isManager() {
        return this.$hasAnyRole([UserRoles.ADMIN, UserRoles.MANAGER]);
    }

    /**
     * Mirrors the columns the table shows on this screen, minus Options. The
     * driver table renders in its expanded form here — `showExpanded` defaults
     * to true and this view does not override it — so the ride, passenger and
     * turnover columns are included, and Company is not: every row already
     * belongs to the selected company, which the file name carries instead.
     *
     * Credit and turnover stay raw numbers rather than the table's "12 NAD"
     * so the columns still add up in a spreadsheet.
     */
    private get driverReportColumns(): ReportColumn<Driver>[] {
        return [
            { header: this.$t('DRIVER.TABLE.SERIAL_ID').toString(), value: (d) => d.serialId },
            { header: this.$t('DRIVER.TABLE.FIRSTNAME').toString(), value: (d) => d.firstName },
            { header: this.$t('DRIVER.TABLE.LASTNAME').toString(), value: (d) => d.lastName },
            { header: this.$t('DRIVER.TABLE.PHONE').toString(), value: (d) => this.toPhone(d) },
            { header: this.$t('DRIVER.TABLE.LICENSE').toString(), value: (d) => d.driverLicense },
            { header: this.$t('DRIVER.TABLE.VERIFIED').toString(), value: (d) => this.toYesNo(d.isVerified) },
            { header: this.$t('COMPANY.TABLE.STATUS').toString(), value: (d) => this.toActive(d.active) },
            { header: this.$t('DRIVER.TABLE.NUMBER_OF_RIDES').toString(), value: (d) => d.numberOfRides ?? 0 },
            { header: this.$t('DRIVER.TABLE.NUMBER_OF_USER').toString(), value: (d) => d.passengerCount ?? 0 },
            { header: this.$t('DRIVER.TABLE.CREDIT').toString(), value: (d) => d.credit ?? 0 },
            { header: this.$t('DRIVER.TABLE.TURN_OVER').toString(), value: (d) => d.transactionAmount ?? 0 },
            { header: this.$t('DRIVER.TABLE.PROMOTER').toString(), value: (d) => this.toPromoterName(d.promoter) }
        ];
    }

    private exportDriversCsv(): void {
        const rows = this.allDrivers ?? [];
        if (!rows.length) {
            this.$notifyInfoSimplified('EXPORT.EMPTY');
            return;
        }

        downloadCsv(
            csvFileName('drivers', this.selectedCompany?.name ?? null, this.startDate, this.endDate),
            toCsv(rows, this.driverReportColumns)
        );
    }

    private async exportDriversPdf(): Promise<void> {
        const rows = this.allDrivers ?? [];
        if (!rows.length) {
            this.$notifyInfoSimplified('EXPORT.EMPTY');
            return;
        }

        await downloadTablePdf({
            fileName: reportFileName('drivers', this.selectedCompany?.name ?? null, this.startDate, this.endDate, 'pdf'),
            title: this.$t('EXPORT.TITLE.DRIVERS').toString(),
            subtitle: reportSubtitle(this.selectedCompany?.name ?? null, this.startDate, this.endDate),
            rows,
            columns: this.driverReportColumns
        });
    }

    private toPhone(driver: Driver): string {
        if (!driver.phone?.prefix || !driver.phone?.phoneNumber) return '';
        return `${driver.phone.prefix} ${driver.phone.phoneNumber}`;
    }

    private toYesNo(value: boolean): string {
        return this.$t(value ? 'EXPORT.YES' : 'EXPORT.NO').toString();
    }

    private toActive(value: boolean): string {
        return this.$t(value ? 'COMPANY.TABLE.ACTIVE' : 'COMPANY.TABLE.INACTIVE').toString();
    }

    private toPromoterName(promoter: string | Promoter | undefined): string {
        if (!promoter || typeof promoter === 'string') return '';
        return `${promoter.firstName ?? ''} ${promoter.lastName ?? ''}`.trim();
    }
}
